import { itemPlayable, type ManifestPlaylist, type PlaylistItem } from '@tbcast/shared';
import { createRenderable } from './items';
import { DEFAULT_TRANSITION_MS, runTransition } from './transitions';
import type { PlaybackContext, Renderable, SequenceItem } from './types';
import { withTimeout } from './types';

const READY_TIMEOUT_MS = 20_000;
const IDLE_RECHECK_MS = 10_000;

/**
 * Flattens a playlist (with nested playlists, PDF/PPTX pages) into a list of
 * sequence items. Cycles are cut off.
 */
export function flattenPlaylist(
  playlistId: string,
  ctx: PlaybackContext,
  seen = new Set<string>(),
): SequenceItem[] {
  const pl = ctx.manifest.playlists.find((p) => p.id === playlistId);
  if (!pl || seen.has(pl.id)) return [];
  seen.add(pl.id);
  const out: SequenceItem[] = [];
  const items = pl.shuffle ? shuffle([...pl.items]) : pl.items;
  for (const it of items) out.push(...expandItem(it, ctx, seen));
  seen.delete(pl.id);
  return out;
}

function expandItem(it: PlaylistItem, ctx: PlaybackContext, seen: Set<string>): SequenceItem[] {
  if (it.kind === 'playlist') {
    // Nested playlist items inherit the outer validity window.
    return flattenPlaylist(it.refId, ctx, seen).map((x) => ({
      ...x,
      key: `${it.id}/${x.key}`,
      validFrom: it.validFrom ?? x.validFrom,
      validUntil: it.validUntil ?? x.validUntil,
      dayparts: it.dayparts?.length ? it.dayparts : x.dayparts,
    }));
  }
  const base: SequenceItem = {
    key: it.id,
    kind: it.kind,
    refId: it.refId,
    durationMs: it.durationMs,
    transition: it.transition,
    validFrom: it.validFrom,
    validUntil: it.validUntil,
    dayparts: it.dayparts,
  };
  if (it.kind === 'media') {
    const m = ctx.manifest.media.find((x) => x.id === it.refId);
    if (m && (m.type === 'pdf' || m.type === 'pptx') && m.pageAssetIds?.length) {
      return m.pageAssetIds.map((_, page) => ({ ...base, key: `${it.id}#${page}`, page }));
    }
  }
  return [base];
}

export function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

export function playlistFromSingle(
  kind: SequenceItem['kind'],
  refId: string,
  durationMs = 10_000,
): ManifestPlaylist {
  return {
    id: `single:${kind}:${refId}`,
    name: '',
    shuffle: false,
    items: [{ id: `single-${refId}`, kind, refId, durationMs, transition: 'fade' }],
  };
}

/**
 * Plays a list of items with double buffering: the next item is created and
 * made ready while the current one is on screen, so there is never a black
 * frame. Each switch runs a GPU-friendly transition.
 */
export class Sequence {
  private current: { r: Renderable; item: SequenceItem; endPoP: () => void } | null = null;
  private next: { r: Renderable; item: SequenceItem; index: number } | null = null;
  private index = -1;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private stopped = false;
  private advancing = false;
  /** Set by tests/monitoring. */
  transitions = 0;

  constructor(
    private container: HTMLElement,
    private items: SequenceItem[],
    private ctx: PlaybackContext,
  ) {}

  get isEmpty(): boolean {
    return this.items.length === 0;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    void this.advance();
  }

  /** Replace the item list (manifest update) without interrupting the current item. */
  setItems(items: SequenceItem[]): void {
    this.items = items;
    this.next?.r.destroy();
    this.next = null;
    this.index = this.current ? Math.min(this.index, items.length - 1) : -1;
  }

  private nextPlayableIndex(from: number): number {
    const now = new Date();
    for (let step = 1; step <= this.items.length; step++) {
      const i = (from + step) % this.items.length;
      if (itemPlayable(this.items[i]!, now, this.ctx.timeZone)) return i;
    }
    return -1;
  }

  /** Builds and readies the renderable for the next playable item (skips broken ones). */
  private async prepare(
    from: number,
  ): Promise<{ r: Renderable; item: SequenceItem; index: number } | null> {
    let cursor = from;
    for (let attempts = 0; attempts < Math.max(1, this.items.length); attempts++) {
      const i = this.nextPlayableIndex(cursor);
      if (i < 0) return null;
      const item = this.items[i]!;
      // A single-item sequence loops its media natively.
      const r = createRenderable(item, this.ctx, { loopMedia: this.items.length === 1 });
      if (r) {
        r.el.style.visibility = 'hidden';
        this.container.appendChild(r.el);
        const ok = await withTimeout(
          r.ready().then(() => true),
          READY_TIMEOUT_MS,
          false,
        );
        if (this.stopped) {
          r.destroy();
          return null;
        }
        if (ok) return { r, item, index: i };
        this.ctx.log('item not ready, skipping', item.key);
        r.destroy();
      }
      cursor = i;
      if (i === from) break;
    }
    return null;
  }

  private async advance(): Promise<void> {
    if (this.stopped || this.advancing) return;
    this.advancing = true;
    try {
      const prepared = this.next ?? (await this.prepare(this.index));
      this.next = null;
      if (this.stopped) {
        prepared?.r.destroy();
        return;
      }
      if (!prepared) {
        // Nothing playable right now (dayparts/validity): keep the current item and re-check later.
        this.timer = setTimeout(() => void this.advance(), IDLE_RECHECK_MS);
        return;
      }
      const previous = this.current;
      const isSame = previous && previous.item.key === prepared.item.key && this.items.length === 1;
      if (isSame) {
        // Single looping item: keep it, drop the duplicate.
        prepared.r.destroy();
      } else {
        prepared.r.play();
        await runTransition(
          prepared.r.el,
          previous?.r.el ?? null,
          previous ? prepared.item.transition : 'fade',
          previous ? DEFAULT_TRANSITION_MS / this.ctx.speed : 300,
        );
        previous?.endPoP();
        previous?.r.destroy();
        const media = this.ctx.manifest.media.find((m) => m.id === prepared.item.refId);
        const endPoP = this.ctx.onItemStart({
          kind: prepared.item.kind,
          id: prepared.item.refId,
          name: media?.name,
        });
        this.current = { r: prepared.r, item: prepared.item, endPoP };
        this.transitions++;
      }
      this.index = prepared.index;
      if (this.stopped) return;
      this.scheduleNext();
    } finally {
      this.advancing = false;
    }
  }

  private scheduleNext(): void {
    const cur = this.current;
    if (!cur) return;
    let fired = false;
    const go = () => {
      if (fired || this.stopped) return;
      fired = true;
      if (this.timer) clearTimeout(this.timer);
      void this.advance();
    };
    const intrinsic = cur.r.intrinsicDurationMs;
    const isVideoLike = !!cur.r.onEnded && cur.item.kind === 'media';
    const duration =
      (isVideoLike && intrinsic
        ? intrinsic
        : cur.item.kind === 'presentation' && intrinsic
          ? intrinsic
          : cur.item.durationMs) / this.ctx.speed;
    if (cur.r.onEnded && this.items.length > 1) cur.r.onEnded(go);
    // Safety net: never stay longer than duration (+ slack for videos whose metadata lied).
    this.timer = setTimeout(
      go,
      Math.max(1000 / this.ctx.speed, duration + (isVideoLike ? 2000 / this.ctx.speed : 0)),
    );
    // Double buffering: prepare the next item well before the switch.
    if (this.items.length > 1) {
      const lead = Math.max(0, duration - 4000 / this.ctx.speed);
      setTimeout(() => {
        if (this.stopped || this.next || fired) return;
        void this.prepare(this.index).then((p) => {
          if (this.stopped || fired) p?.r.destroy();
          else this.next = p;
        });
      }, lead);
    }
  }

  stop(): void {
    this.stopped = true;
    this.running = false;
    if (this.timer) clearTimeout(this.timer);
  }

  destroy(): void {
    this.stop();
    this.current?.endPoP();
    this.current?.r.destroy();
    this.next?.r.destroy();
    this.current = null;
    this.next = null;
  }
}
